import { binCentreStation, type SuperBin } from '@/lib/stationVolumes/aggregate';
import { BAR_RADIUS_PX, barPath, type crossfallPaths, TICK_FONT } from '@/lib/stationVolumes/chartLayout';
import { coverageBand } from '@/lib/stationVolumes/coverageBands';
import { DockChartLegend, DockSwatch, type DockLegendItem } from '@/components/dock/DockChartLegend';
import { AxisLeft } from '@visx/axis';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';

/** The linear scales the panes draw with — `@visx/scale`'s own return type. */
type LinearScale = ReturnType<typeof scaleLinear<number>>;

/** Axis tick size, shared so both panes and the station axis agree. */
export { TICK_FONT };

/**
 * Presentational pieces of `StationVolumeChart` — the legend row, the hover
 * tooltip card, the visually-hidden data table, and the chart's own CSS.
 *
 * They live here, not in the chart, so the chart file is the drawing and the
 * interaction and nothing else. Every one of them takes finished strings: the
 * chart owns `t`, these own markup.
 */

/**
 * Chart-local CSS. The palette itself lives in `SV_TOKENS` on the dock root;
 * these are the rules that need the chart's own element ids (the hatch
 * patterns) or its own motion.
 */
export const CHART_CSS = `
.sv-chart:focus-visible{outline:2px solid var(--sv-walk);outline-offset:2px;border-radius:4px;}
/* The brush handles are not separate tab stops (the chart is one tab stop,
   spec §4b), so their focus affordance is "the chart has focus and there is
   a brush" — Shift+arrows then move the trailing handle. */
.sv-chart:focus-visible .sv-handle{stroke-width:3;}
.sv-walk-cursor,.sv-hover-cursor{pointer-events:none;}
@media (prefers-reduced-motion:no-preference){
  .sv-walk-cursor{transition:transform 120ms cubic-bezier(.2,0,0,1);}
}
/* Forced colours: the tokens already remap to system colours; swap the solid
   fills for hatches so cut and fill stay separable when every hue collapses. */
@media (forced-colors:active){
  .sv-bar-fill{fill:url(#sv-hatch-fill);}
  .sv-bar-cut{fill:url(#sv-hatch-cut);}
  /* The baseline is the datum every bar is read against; in forced colours it
     is drawn at full strength, not at the 35 % that keeps it quiet on our own
     surface. */
  .sv-zero-line{stroke:CanvasText;stroke-opacity:1;}
}
`;

export const FLOATING_TOOLTIP_STYLE = { position: 'absolute', zIndex: 9999, pointerEvents: 'none' } as const;

/**
 * One tooltip row: label left, figure right, tabular so columns line up.
 *
 * A row may carry an `ink`, which colours its LABEL. That is for rows whose
 * series is identified by hue in the pane above — the crossfall pane's two
 * sides — so the reader can match the figure to the line without a legend
 * lookup. The figure itself stays in the card's own ink: colouring the number
 * would make the value look like a status.
 */
export const TooltipCard = ({
  station,
  rows,
}: {
  station: string;
  rows: { label: string; value: string; ink?: string }[];
}) => (
  <div className='min-w-40'>
    <div className='sv-sta mb-1 text-[11px] opacity-90'>{station}</div>
    <div className='sv-num flex flex-col gap-0.5'>
      {/* Keyed by position, not label: one grouped bar can hold two station
          equations, and two rows sharing a label is legitimate data. */}
      {rows.map((r, i) => (
        <div key={`${i}:${r.label}`} className='flex items-baseline justify-between gap-4'>
          <span className={r.ink ? '' : 'opacity-75'} style={r.ink ? { color: r.ink } : undefined}>
            {r.label}
          </span>
          <span>{r.value}</span>
        </div>
      ))}
    </div>
  </div>
);

/**
 * The visually-hidden data table that satisfies "a table view exists" without
 * a second UI (spec §5 A11y point 4). It carries what is *drawn*: when the
 * bins are super-binned the Table tab is the full-resolution view, and the
 * caption says so.
 */
export const HiddenDataTable = ({
  caption,
  headers,
  rows,
  currentRow,
}: {
  caption: string;
  headers: string[];
  rows: { key: number; cells: string[] }[];
  currentRow: number | null;
}) => (
  <table className='sr-only'>
    {/* `sr-only` on the <table> does NOT hide the <caption>. A table element's
        non-inheritable properties (width/height/overflow/clip-path) are used on
        the inner table box, while position/margin go to the table WRAPPER box —
        and <caption> is a child of that wrapper, so it escapes the clip and
        paints at full size. Worse, the wrapper is an abspos child of a flex
        column with no top/left, so its static position is the flex container's
        content-box start corner: the caption landed on top of the pane's first
        row (the section readout). Clip the caption itself as well — the same
        pattern the visible tables use (StationVolumeTable, ClearanceTable). */}
    <caption className='sr-only'>{caption}</caption>
    <thead>
      <tr>
        {headers.map((h) => (
          <th key={h} scope='col'>
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key} aria-current={r.key === currentRow ? 'location' : undefined}>
          <th scope='row'>{r.cells[0]}</th>
          {r.cells.slice(1).map((c, i) => (
            <td key={headers[i + 1]}>{c}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * The legend is always present — cut and fill have to be identifiable without
 * hovering anything — and it carries the two honesty chips beside it: the
 * coverage scale the strip under the bars is drawn in, and the super-bin
 * factor when neighbouring bins have been summed.
 *
 * Drawn through the shared `DockChartLegend` (one 20 px row that never
 * wraps), so both docks' charts key their marks the same way.
 */
export const ChartLegend = ({
  fillLabel,
  cutLabel,
  cutConvention,
  fillConvention,
  coverageScale,
  groupedLabel,
  groupedHelp,
  hint,
  massHaul,
  lowerPane,
  crossfallLegend,
}: {
  /**
   * The series names, ONE phrase each, stating the convention (T4, closing
   * F21): "Cut (above design)" — cut is material sitting above the design,
   * whichever side of the zero line the bar is drawn on.
   */
  fillLabel: string;
  cutLabel: string;
  /**
   * The cut/fill convention's full sentence, the swatch's tooltip. `short`
   * is no longer printed — the label above carries it — and is kept only so
   * callers need not change.
   */
  cutConvention: { short?: string; full: string };
  fillConvention: { short?: string; full: string };
  /** The five coverage bands (`coverageBands.ts`), lowest first. */
  coverageScale: { label: string; help: string; bands: { color: string; label: string }[] };
  /** `null` unless the chart is drawing super-bins. */
  groupedLabel: string | null;
  groupedHelp: string;
  hint: string;
  /** `null` outside compact mode, where both panes are shown at once. */
  massHaul: { label: string; help: string; on: boolean; onToggle: () => void } | null;
  /**
   * The crossfall pane's four marks, shown only while that pane is on.
   *
   * It needs a VISIBLE key, unlike the other two panes: it is the only pane
   * that draws four lines whose sole difference is hue, and an SVG `<title>`
   * on a 2 px stroke needs the reader to find and hover that stroke first.
   * The two side swatches carry the pane's own inks; design and tolerance are
   * drawn in `currentColor`, because each of those exists in BOTH inks and a
   * swatch in one of them would claim otherwise.
   */
  crossfallLegend: { left: string; right: string; design: string; tolerance: string } | null;
  /**
   * What the lower pane plots. `null` on a run with no true station-line
   * areas — there is nothing to switch to, so no control is offered rather
   * than a disabled one nobody can act on.
   */
  lowerPane: {
    value: 'haul' | 'endArea' | 'crossfall';
    onChange: (v: 'haul' | 'endArea' | 'crossfall') => void;
    haulLabel: string;
    endAreaLabel: string;
    /** Absent on a run with no crossfall check: the option is then not
     *  offered at all rather than offered disabled. */
    crossfallLabel?: string;
    /** Per-option explanations. "End area" is the term readers most often
     *  mistake for a volume, so each option carries its own sentence rather
     *  than sharing one on the group. */
    haulHelp: string;
    endAreaHelp: string;
    crossfallHelp?: string;
    groupLabel: string;
    help: string;
  } | null;
}) => {
  // One phrase per series (audit §A9): "Fill (below design)", with the full
  // convention sentence as the tooltip. The old pair — "Fill, above the line"
  // then "Fill · below design" — described the drawing and the ground in two
  // clauses that read as a contradiction.
  const items: DockLegendItem[] = [
    {
      key: 'fill',
      testId: 'sv-legend-convention',
      label: fillLabel,
      title: fillConvention.full,
      color: 'var(--sv-fill)',
      shape: 'barUp',
    },
    {
      key: 'cut',
      testId: 'sv-legend-convention',
      label: cutLabel,
      title: cutConvention.full,
      color: 'var(--sv-cut)',
      shape: 'barDown',
    },
    {
      key: 'coverage',
      testId: 'sv-legend-coverage',
      title: coverageScale.help,
      // The scale IS the swatch: five 10 × 6 chips, each with its range.
      swatch: <></>,
      label: (
        <span className='flex items-center gap-1'>
          {coverageScale.label}
          {coverageScale.bands.map((b) => (
            <span key={b.label} className='flex items-center gap-0.5'>
              <svg width={12} height={8} aria-hidden>
                <rect x={1} y={1} width={10} height={6} fill={b.color} />
              </svg>
              <span className='sv-num'>{b.label}</span>
            </span>
          ))}
          <span>%</span>
        </span>
      ),
    },
  ];
  if (crossfallLegend) {
    // One item holding the pane's four marks, so the key appears and goes
    // with the pane as one unit (`sv-crossfall-legend`).
    items.push({
      key: 'crossfall',
      testId: 'sv-crossfall-legend',
      swatch: <></>,
      label: (
        <span className='flex items-center gap-3'>
          <span className='flex items-center gap-1'>
            <DockSwatch color='var(--sv-xf-left)' shape='line' />
            {crossfallLegend.left}
          </span>
          <span className='flex items-center gap-1'>
            <DockSwatch color='var(--sv-xf-right)' shape='line' />
            {crossfallLegend.right}
          </span>
          <span className='flex items-center gap-1'>
            <DockSwatch shape='dashed' />
            {crossfallLegend.design}
          </span>
          <span className='flex items-center gap-1'>
            <DockSwatch shape='band' />
            {crossfallLegend.tolerance}
          </span>
        </span>
      ),
    });
  }
  const segBtn = (on: boolean) =>
    `flex h-5 items-center px-2 whitespace-nowrap not-first:border-l not-first:border-slate-300 dark:not-first:border-stone-600 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none focus-visible:ring-inset ${
      on
        ? 'bg-slate-200 font-semibold text-slate-900 dark:bg-stone-700 dark:text-white'
        : 'hover:bg-slate-100 dark:hover:bg-stone-800'
    }`;
  const right: ReactNode = (
    <>
      {groupedLabel && (
        <span className='sv-chip sv-num' data-testid='sv-grouped' title={groupedHelp}>
          {groupedLabel}
        </span>
      )}
      {lowerPane && (
        // A segmented control, not two independent toggles: the lower pane
        // shows exactly one of these, and a pair of checkboxes would imply a
        // state (both, or neither) that does not exist.
        <span
          role='radiogroup'
          aria-label={lowerPane.groupLabel}
          title={lowerPane.help}
          data-testid='sv-lowerpane-group'
          className='flex items-stretch overflow-hidden rounded border border-slate-300 dark:border-stone-600'
        >
          {(
            [
              ['haul', lowerPane.haulLabel, lowerPane.haulHelp],
              ['endArea', lowerPane.endAreaLabel, lowerPane.endAreaHelp],
              ...(lowerPane.crossfallLabel
                ? ([['crossfall', lowerPane.crossfallLabel, lowerPane.crossfallHelp ?? '']] as const)
                : []),
            ] as const
          ).map(([value, label, optionHelp]) => {
            const on = lowerPane.value === value;
            return (
              <button
                key={value}
                type='button'
                role='radio'
                aria-checked={on}
                title={optionHelp}
                data-testid={`sv-lowerpane-${value}`}
                onClick={() => lowerPane.onChange(value)}
                className={segBtn(on)}
              >
                {label}
              </button>
            );
          })}
        </span>
      )}
      {massHaul && (
        <button
          type='button'
          data-testid='sv-masshaul-toggle'
          aria-pressed={massHaul.on}
          title={massHaul.help}
          onClick={massHaul.onToggle}
          className={`flex h-5 items-center rounded border px-2 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
            massHaul.on ? 'border-current font-semibold' : 'border-slate-300 dark:border-stone-600'
          }`}
        >
          {massHaul.label}
        </button>
      )}
      {/* The interaction hint as an ⓘ, as on the clearance chart: a sentence
          in the row took the width the legend needs. */}
      <span
        data-testid='sv-chart-hint'
        role='img'
        aria-label={hint}
        title={hint}
        className='flex cursor-help items-center opacity-70'
      >
        <Info className='size-3.5' aria-hidden />
      </span>
    </>
  );
  return <DockChartLegend testId='sv-chart-legend' items={items} right={right} />;
};

/** Axis tick label props, shared by both panes and the station axis. */
export const tickLabelProps = (extra?: Record<string, unknown>) => () => ({
  fill: 'currentColor',
  fontSize: TICK_FONT,
  ...extra,
});

/**
 * Rendered width of a tick label, estimated: tick labels are tabular figures
 * (`sv-num` / `sv-sta`) at `TICK_FONT`, ≈ 0.62 em a character. An estimate
 * rather than `getComputedTextLength`, because the gutter and the anchors are
 * needed during render (before any text exists to measure) and jsdom measures
 * every string as 0 px wide.
 */
export const approxLabelPx = (label: string): number => Math.ceil(label.length * TICK_FONT * 0.62);

/**
 * Station-axis tick label props that keep the LAST label inside the plot box:
 * a label that would run past `maxX` (the inner width plus the right margin)
 * is anchored at its end instead of centred on its tick. Audit §A4 — the end
 * label printed "13+20(" at every width, in both docks.
 */
export const stationTickLabelProps =
  (xOf: (v: number) => number, label: (v: number) => string, maxX: number, extra?: Record<string, unknown>) =>
  (value: unknown) => {
    const v = Number(value);
    const overflows = xOf(v) + approxLabelPx(label(v)) / 2 > maxX;
    return {
      fill: 'currentColor',
      fontSize: TICK_FONT,
      ...extra,
      textAnchor: overflows ? ('end' as const) : ('middle' as const),
    };
  };

const MINUS = '\u2212';

/**
 * A value-axis tick at the AXIS's own precision (audit §A9), not the
 * dataset's readout precision: the decimals come from the tick step (0 dp for
 * a step of 1 or more, which covers every axis that reaches 100), the figure
 * is grouped, and a negative carries the house U+2212 — "−2,000", never
 * "-2000.000". A tick is a place to read the axis by; the tooltip and the
 * table keep the full figure.
 */
export const axisTickFormatter = (ticks: readonly number[], minDigits = 0): ((v: number) => string) => {
  let step = Infinity;
  for (let i = 1; i < ticks.length; i++) step = Math.min(step, Math.abs(ticks[i] - ticks[i - 1]));
  const fromStep = !Number.isFinite(step) || step >= 1 ? 0 : Math.min(3, Math.ceil(-Math.log10(step) - 1e-9));
  const digits = Math.max(minDigits, fromStep);
  return (v: number) => {
    const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    // A value that rounds to zero loses its sign, as everywhere in this dock.
    return v < 0 && /[1-9]/.test(s) ? `${MINUS}${s}` : s;
  };
};

/**
 * The bar pane: cut and fill diverging around zero, fill above and cut below.
 *
 * The sign is a STRUCTURAL encoding, so cut and fill survive full
 * colour-blindness and greyscale print with no legend at all.
 *
 * Coverage is NOT drawn on the bars (cut is already red): a strip under the
 * pane carries it, one segment per bar in its `coverageBands.ts` colour.
 * It replaced the old 55 %-opacity + hatch treatment (2026-10-05).
 */
export const BarPane = ({
  bars,
  show,
  xScale,
  yScale,
  barW,
  paneHeight,
  innerWidth,
  marginLeft,
  selLo,
  selHi,
  hoverGroup,
  extremes,
  axisLabel,
  formatVolume,
  stripY,
  stripH,
  coverageTitle,
}: {
  bars: SuperBin[];
  show: boolean;
  xScale: LinearScale;
  yScale: LinearScale;
  barW: number;
  paneHeight: number;
  innerWidth: number;
  marginLeft: number;
  /** Brushed range in ORIGINAL bin indices; `-1` when there is no brush. */
  selLo: number;
  selHi: number;
  hoverGroup: number | null;
  /** Indices into `bars` of the extreme cut and fill bars, or `-1`. */
  extremes: { cut: number; fill: number };
  axisLabel: string;
  /** The chart's dataset-wide m³ formatter (`m3Formatter(...).volume`), so a
   *  direct label and the tooltip over the same bar carry the same decimals. */
  formatVolume: (v: number) => string;
  /** Top of the coverage strip, in pane coordinates, and its height. */
  stripY: number;
  stripH: number;
  coverageTitle: (pct: number) => string;
}) => {
  if (!show) return null;
  const zero = yScale(0);
  const hasSel = selLo >= 0;
  const ticks = yScale.ticks(4);
  const tickText = axisTickFormatter(ticks);
  return (
    <Group>
      {ticks.map((tick) => (
        <line
          key={`gb-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      {/* The axis's own precision (`axisTickFormatter`), not the readout's:
          "−2,000", never "-2000.000" (audit §A9). The direct labels below
          keep the dataset formatter — they quote a bar. */}
      <AxisLeft
        scale={yScale}
        tickValues={ticks}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => tickText(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
      </text>
      {/* The zero baseline is the datum the whole pane is read against, so it
          is drawn in ink: `currentColor` at 35 %, which is a step darker than
          the gridlines and clearly visible on both surfaces. `--sv-zero`
          measured 1.48 : 1 against the dark surface — invisible (ruling A15);
          it stays the neutral *fill* token (the 3D ramp's midpoint) and is no
          longer used as a stroke anywhere. 1.5 px rather than 1 px is what
          separates "the axis" from "a gridline". */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={zero}
        y2={zero}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />

      {bars.map((b, i) => {
        const x = xScale(binCentreStation(b)) - barW / 2;
        const inSel = !hasSel || (b.toBin >= selLo && b.fromBin <= selHi);
        const fillH = Math.max(0, zero - yScale(b.fillVolumeM3));
        const cutH = Math.max(0, yScale(-b.cutVolumeM3) - zero);
        // Unselected bars 0.45 rather than the spec's 0.35 (ruling A15).
        const hovered = i === hoverGroup;
        const opacity = hovered || inSel ? 1 : 0.45;
        const band = coverageBand(b.coveragePct);
        return (
          <g key={b.i} opacity={opacity} data-testid={hovered ? 'sv-bar-hover' : undefined}>
            {b.fillVolumeM3 > 0 && (
              <path
                className='sv-bar-fill'
                d={barPath(x, yScale(b.fillVolumeM3), barW, fillH, BAR_RADIUS_PX, 'top')}
                fill='var(--sv-fill)'
              />
            )}
            {b.cutVolumeM3 > 0 && (
              <path
                className='sv-bar-cut'
                d={barPath(x, zero, barW, cutH, BAR_RADIUS_PX, 'bottom')}
                fill='var(--sv-cut)'
              />
            )}
            {/* The strip runs the bin's full station span, so it reads as one
                continuous track under the bars rather than a row of chips. */}
            <rect
              data-testid='sv-coverage-seg'
              data-band={band.index}
              x={xScale(b.staStartInternalM)}
              y={stripY}
              width={Math.max(1, xScale(b.staEndInternalM) - xScale(b.staStartInternalM))}
              height={stripH}
              fill={band.color}
            >
              <title>{coverageTitle(b.coveragePct)}</title>
            </rect>
          </g>
        );
      })}

      {/* Direct labels on the extreme cut bar and the extreme fill bar only.
          A number on every bar is a table, badly drawn. */}
      {extremes.fill >= 0 && (
        <text
          data-testid='sv-label-fill'
          x={xScale(binCentreStation(bars[extremes.fill]))}
          y={Math.max(9, yScale(bars[extremes.fill].fillVolumeM3) - 4)}
          textAnchor='middle'
          fontSize={TICK_FONT}
          className='sv-num'
          fill='var(--sv-fill)'
        >
          {formatVolume(bars[extremes.fill].fillVolumeM3)}
        </text>
      )}
      {extremes.cut >= 0 && (
        <text
          data-testid='sv-label-cut'
          x={xScale(binCentreStation(bars[extremes.cut]))}
          y={Math.min(paneHeight - 1, yScale(-bars[extremes.cut].cutVolumeM3) + 10)}
          textAnchor='middle'
          fontSize={TICK_FONT}
          className='sv-num'
          fill='var(--sv-cut)'
        >
          {/* Negative, because the shared volume formatter keeps the sign, so
              a bare magnitude here would read as a
              contradiction against the −100 tick beside it. Matches the PDF
              chart (`processReport/stationChart.ts`). */}
          {formatVolume(-bars[extremes.cut].cutVolumeM3)}
        </text>
      )}
    </Group>
  );
};

/**
 * The mass-haul pane: cumulative net volume, with a diamond on every zero
 * crossing. `--sv-haul` is neutral ink rather than a series hue, because the
 * curve is a derived quantity and not a third category (spec §4b).
 */
export const HaulPane = ({
  show,
  top,
  path,
  xScale,
  yScale,
  innerWidth,
  marginLeft,
  balance,
  balanceLabels,
  axisLabel,
}: {
  show: boolean;
  top: number;
  path: string;
  xScale: LinearScale;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  /** Internal stations of the zero crossings. */
  balance: number[];
  /** Label per crossing, `null` where it would collide with its neighbour. */
  balanceLabels: (string | null)[];
  axisLabel: string;
  /** @deprecated Unused: the ticks carry the axis's own precision now. */
  formatVolume?: (v: number) => string;
}) => {
  if (!show) return null;
  const zero = yScale(0);
  const ticks = yScale.ticks(3);
  const tickText = axisTickFormatter(ticks);
  const paneH = Math.max(...yScale.range());
  return (
    <Group top={top}>
      {/* Same hairline grid as the bar pane, so the two read as one
          instrument rather than a chart and a sparkline. */}
      {ticks.map((tick) => (
        <line
          key={`gh-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        tickValues={ticks}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => tickText(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
      </text>
      {/* Same datum, same ink as the bar pane's — the two panes have to read
          as one instrument. */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={zero}
        y2={zero}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />
      <path
        data-testid='sv-haul-path'
        d={path}
        fill='none'
        stroke='var(--sv-haul)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      {balance.map((s, i) => (
        <g key={`bp-${s}`}>
          <rect
            data-testid='sv-balance'
            x={xScale(s) - 5}
            y={zero - 5}
            width={10}
            height={10}
            transform={`rotate(45 ${xScale(s)} ${zero})`}
            fill='var(--sv-haul)'
            stroke='var(--sv-surface)'
            strokeWidth={2}
          />
          {balanceLabels[i] &&
            (() => {
              // Centred ABOVE the diamond (it sat beside it, on the curve
              // that crosses there), flipped below when the pane's top would
              // clip it, and anchored to the plot edge rather than run off
              // it. A surface-coloured halo keeps the curve from striking
              // through the text (audit §A9).
              const label = balanceLabels[i]!;
              const x = xScale(s);
              const half = approxLabelPx(label) / 2;
              const anchor = x - half < 0 ? 'start' : x + half > innerWidth ? 'end' : 'middle';
              const lx = anchor === 'start' ? Math.max(0, x - 4) : anchor === 'end' ? Math.min(innerWidth, x + 4) : x;
              const above = zero - 9;
              const y = above - TICK_FONT < 0 ? Math.min(paneH - 1, zero + 9 + TICK_FONT) : above;
              return (
                <text
                  data-testid='sv-balance-label'
                  x={lx}
                  y={y}
                  textAnchor={anchor}
                  fontSize={TICK_FONT}
                  className='sv-num'
                  fill='currentColor'
                  stroke='var(--sv-surface)'
                  strokeWidth={3}
                  strokeLinejoin='round'
                  paintOrder='stroke'
                >
                  {label}
                </text>
              );
            })()}
        </g>
      ))}
    </Group>
  );
};

/**
 * The end-area pane: cut and fill cross-section area in m² at every station
 * LINE — the answer to "what is the area between the surfaces at this
 * chainage", which the bar pane's m³ cannot give.
 *
 * A polyline per side rather than diverging bars, and a `[0, max]` domain
 * rather than one centred on zero: an area has no sign. Cut and fill are two
 * series here, not two directions, and they carry the same two hues the bars
 * and the section bands already use so the three views read as one instrument.
 */
export const EndAreaPane = ({
  show,
  top,
  cutPath,
  fillPath,
  yScale,
  innerWidth,
  marginLeft,
  axisLabel,
  axisHelp,
}: {
  show: boolean;
  top: number;
  cutPath: string;
  fillPath: string;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  axisLabel: string;
  /** Rendered as an SVG <title>, so hovering the axis label explains the
   *  quantity to a reader who never touches the pane control. */
  axisHelp?: string;
}) => {
  if (!show) return null;
  return (
    <Group top={top}>
      {yScale.ticks(3).map((tick) => (
        <line
          key={`ge-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        tickValues={yScale.ticks(3)}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => axisTickFormatter(yScale.ticks(3))(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
        {axisHelp && <title>{axisHelp}</title>}
      </text>
      {/* Zero is the floor here, not a datum the series straddles. */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={yScale(0)}
        y2={yScale(0)}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />
      <path
        data-testid='sv-endarea-fill-path'
        d={fillPath}
        fill='none'
        stroke='var(--sv-fill)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        data-testid='sv-endarea-cut-path'
        d={cutPath}
        fill='none'
        stroke='var(--sv-cut)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Group>
  );
};

/**
 * The crossfall pane: as-built left/right solid, design left/right dashed,
 * the tolerance band shaded around each design line.
 *
 * Its own pane and its own scale for the same reason the end-area pane has
 * one: percent and m³ share no datum, and on a dual-scale axis the crossing
 * of two series is an artefact of the scales.
 *
 * The two inks are `--sv-xf-left` / `--sv-xf-right`, NOT the cut/fill pair:
 * left and right are two sides of the road, a category, and the diverging
 * pair means "either side of zero" everywhere else in this dock.
 */
export const CrossfallPane = ({
  show,
  top,
  paths,
  yScale,
  innerWidth,
  marginLeft,
  axisLabel,
  axisHelp,
  leftLabel,
  rightLabel,
  designLabel,
  toleranceLabel,
}: {
  show: boolean;
  top: number;
  paths: ReturnType<typeof crossfallPaths>;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  axisLabel: string;
  /** Rendered as an SVG <title>, so hovering the axis label explains the
   *  quantity to a reader who never touches the pane control. */
  axisHelp?: string;
  /** Named on every mark as an SVG <title>: with four lines in two hues, the
   *  identity of each one has to survive a reader who never hovers the
   *  tooltip and a reader who cannot see the two hues apart. */
  leftLabel: string;
  rightLabel: string;
  designLabel: string;
  toleranceLabel: string;
}) => {
  if (!show) return null;
  const line = (d: string, ink: string, testId: string, dashed: boolean, name: string) =>
    d ? (
      <path
        data-testid={testId}
        d={d}
        fill='none'
        stroke={ink}
        strokeWidth={dashed ? 1.5 : 2}
        strokeDasharray={dashed ? '5,3' : undefined}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <title>{name}</title>
      </path>
    ) : null;
  return (
    <Group top={top}>
      {yScale.ticks(3).map((tick) => (
        <line
          key={`gx-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        numTicks={3}
        stroke='currentColor'
        tickStroke='currentColor'
        // At least 1 dp — a crossfall is read in tenths of a percent — and
        // the house minus, which `toFixed` never printed.
        tickFormat={(v) => axisTickFormatter(yScale.ticks(3), 1)(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
        {axisHelp && <title>{axisHelp}</title>}
      </text>
      {/* The tolerance first, under everything: it is the ground the two
          series are read against, never a mark on top of them. */}
      {paths.toleranceLeft && (
        <path
          data-testid='sv-crossfall-tolerance-left'
          d={paths.toleranceLeft}
          fill='var(--sv-xf-left)'
          fillOpacity={0.12}
          stroke='none'
        >
          <title>{`${toleranceLabel} · ${leftLabel}`}</title>
        </path>
      )}
      {paths.toleranceRight && (
        <path
          data-testid='sv-crossfall-tolerance-right'
          d={paths.toleranceRight}
          fill='var(--sv-xf-right)'
          fillOpacity={0.12}
          stroke='none'
        >
          <title>{`${toleranceLabel} · ${rightLabel}`}</title>
        </path>
      )}
      {line(paths.designLeft, 'var(--sv-xf-left)', 'sv-crossfall-design-left-path', true, `${designLabel} · ${leftLabel}`)}
      {line(
        paths.designRight,
        'var(--sv-xf-right)',
        'sv-crossfall-design-right-path',
        true,
        `${designLabel} · ${rightLabel}`,
      )}
      {line(paths.asbuiltLeft, 'var(--sv-xf-left)', 'sv-crossfall-asbuilt-left-path', false, leftLabel)}
      {line(paths.asbuiltRight, 'var(--sv-xf-right)', 'sv-crossfall-asbuilt-right-path', false, rightLabel)}
    </Group>
  );
};
